import { useCallback, useMemo, useState } from 'react';
import type { FormIdentity, SuggestTarget } from '@capture/shared';
import { useFormConfigStore } from '../state/store';
import { SampleTreePane } from './SampleTreePane';
import { IdentityEditor } from './IdentityEditor';
import { HeaderFieldsPane } from './HeaderFieldsPane';
import { BatchValidationSummary } from './BatchValidationSummary';
import { useXPathSuggester } from './hooks/useXPathSuggester';
import { parseXML, parseJSON } from '@capture/shared';
import './HeaderTab.css';

/**
 * The "Header" tab in the form-config editor. Wires together:
 *
 *   - Format radio (XML / JSON)
 *   - `SampleTreePane` — paste-a-sample + clickable tree
 *   - `IdentityEditor` — the form's single identity clause
 *   - `HeaderFieldsPane` — inline list of header-mapped fields
 *   - `BatchValidationSummary` — publish-gate summary + header.* messages
 *   - `useXPathSuggester` — bridges tree clicks into identity / field
 *     path input (currently focused on the identity path; field-row
 *     integration arrives in later WUs).
 */
export function HeaderTab() {
  const content = useFormConfigStore((s) => s.content);
  const setIdentity = useFormConfigStore((s) => s.setIdentity);
  const [format, setFormat] = useState<'xml' | 'json'>('xml');
  const [sampleRaw, setSampleRaw] = useState<string | null>(null);
  const [lastClickedPath, setLastClickedPath] = useState<string | null>(null);

  const parsedSampleRoot = useMemo(() => {
    if (!sampleRaw) return null;
    const p = format === 'xml' ? parseXML(sampleRaw) : parseJSON(sampleRaw);
    if (!p.ok) return null;
    return 'doc' in p ? p.doc : p.root;
  }, [sampleRaw, format]);

  const { suggestForTarget } = useXPathSuggester(parsedSampleRoot, format);

  const identity: FormIdentity =
    content?.identity ??
    (format === 'xml'
      ? { format: 'xml', xpath: '', op: 'eq', value: '' }
      : { format: 'json', path: '', op: 'eq', value: '' });

  const handleNodeClick = useCallback(
    (target: SuggestTarget) => {
      const suggestion = suggestForTarget(target);
      if (!suggestion) return;
      setLastClickedPath(suggestion.xpath);
      // Phase 1: tree-click no longer auto-seeds Identity. Identity has its
      // own "← Use last-clicked path" button. HeaderFieldComposer (Task 10)
      // will also consume lastClickedPath.
    },
    [suggestForTarget],
  );

  if (!content) return null;

  return (
    <div className="header-tab">
      <div className="header-tab__topbar">
        <fieldset className="header-tab__format">
          <legend>Format</legend>
          <label>
            <input
              type="radio"
              name="header-tab-format"
              checked={format === 'xml'}
              onChange={() => setFormat('xml')}
            />
            XML
          </label>
          <label>
            <input
              type="radio"
              name="header-tab-format"
              checked={format === 'json'}
              onChange={() => setFormat('json')}
            />
            JSON
          </label>
        </fieldset>
      </div>
      <div className="header-tab__panes">
        <aside className="header-tab__pane header-tab__pane--sample">
          <SampleTreePaneWithCallback
            format={format}
            onNodeClick={handleNodeClick}
            onRawChange={setSampleRaw}
          />
        </aside>
        <div className="header-tab__pane header-tab__pane--editor">
          <IdentityEditor
            identity={identity}
            sampleRaw={sampleRaw}
            onChange={setIdentity}
            lastClickedPath={lastClickedPath}
          />
          <HeaderFieldsPane
            format={format}
            lastClickedPath={lastClickedPath}
            onComposerClose={() => setLastClickedPath(null)}
          />
          <BatchValidationSummary />
        </div>
      </div>
    </div>
  );
}

/**
 * Wrapper that forwards the pasted raw text up to HeaderTab so the
 * IdentityEditor's test-against-sample and the suggester hook share
 * the same sample. SampleTreePane owns its own textarea state; this
 * wrapper subscribes via a hidden onChange bridge.
 *
 * Kept as an internal wrapper rather than extending SampleTreePane's
 * public API to avoid breaking the standalone-component contract.
 */
function SampleTreePaneWithCallback({
  format,
  onNodeClick,
  onRawChange,
}: {
  format: 'xml' | 'json';
  onNodeClick: (target: SuggestTarget) => void;
  onRawChange: (raw: string | null) => void;
}) {
  // Sniff the textarea value via a ref-style effect — SampleTreePane
  // doesn't expose its internal raw state, so we listen for onChange
  // events at the DOM boundary via event delegation.
  return (
    <div
      onInput={(e) => {
        const target = e.target as HTMLElement;
        if (target instanceof HTMLTextAreaElement) {
          onRawChange(target.value === '' ? null : target.value);
        }
      }}
    >
      <SampleTreePane format={format} onNodeClick={onNodeClick} />
    </div>
  );
}
